import { ArrowUpRight, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* ================= Block 1 — Page hero ================= */

const ProgramsHero = () => (
  <section data-testid="programs-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="grid gap-12 lg:grid-cols-[560px_1fr] lg:items-center lg:gap-16">
        {/* Left — editorial text */}
        <div className="lg:self-start">
          <div className="flex items-center justify-center gap-3 sm:justify-start" aria-hidden="true">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 sm:hidden" />
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
              Programs
            </span>
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))] sm:hidden" />
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 sm:hidden" />
          </div>
          <h1 className="font-serif-editorial mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
            Build Working Knowledge for Modern Business Finance
          </h1>
          <p className="mt-8 text-base leading-relaxed text-muted-foreground sm:text-lg lg:mt-[47px]">
            WFII programs are designed for finance professionals, accountants,
            bookkeepers, auditors, assurance professionals, CFOs, treasury
            teams, founders, and business leaders who want to understand how
            digital money, stablecoins, digital assets, and new payment
            infrastructure are changing business operations.
          </p>
          <p className="mt-4 text-center text-base leading-relaxed text-muted-foreground sm:text-left sm:text-lg">
            Each program focuses on clear concepts, business use cases,
            operational workflows, and the key questions finance teams need{" "}
            <br className="hidden lg:block" />
            to understand before working with{" "}
            <br className="sm:hidden" aria-hidden="true" />
            digital finance tools.
          </p>
        </div>

        {/* Right — small photo collage */}
        <div className="grid grid-cols-2 gap-5" data-testid="programs-hero-photos">
          <div className="pt-8">
            <div className="overflow-hidden rounded-2xl shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10">
              <img
                src="/programs-hero-3.jpg"
                alt="Upward view of a modern glass skyscraper against the sky"
                className="wfii-photo aspect-[4/5] w-full object-cover"
                loading="eager"
                data-testid="programs-hero-photo-1"
              />
            </div>
          </div>
          <div className="flex flex-col gap-5">
            <div className="overflow-hidden rounded-2xl shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10">
              <img
                src="/programs-hero-1.jpg"
                alt="Panel discussion with a business leader being interviewed on stage"
                className="wfii-photo aspect-[4/3] w-full object-cover lg:aspect-[40/33]"
                loading="eager"
                data-testid="programs-hero-photo-2"
              />
            </div>
            <div className="overflow-hidden rounded-2xl shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10">
              <img
                src="/programs-hero-2.jpg"
                alt="Large lecture hall audience listening to a speaker"
                className="wfii-photo aspect-[4/3] w-full object-cover lg:aspect-[40/33]"
                loading="lazy"
                data-testid="programs-hero-photo-3"
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — Featured programs ================= */

const PROGRAM_CARDS = [
  {
    title: "Digital Money & Stablecoins",
    desc: "Build a clear foundation in stablecoins, wallets, digital payment rails, and business use cases.",
    bestFor: "CFOs, accountants, bookkeepers, auditors, founders, and finance teams.",
    format: ["5 modules", "10 learning hours", "Certificate of completion"],
    img: "/program-stablecoins.jpg",
    alt: "Conference audience at a business finance industry event",
    id: "digital-money-stablecoins",
    href: "/programs/digital-money-and-stablecoins",
  },
  {
    title: "Digital Asset Accounting",
    desc: "Learn how to approach digital asset records, reconciliation, reporting, and accounting workflows.",
    bestFor:
      "Accountants, bookkeepers, controllers, finance teams, and professionals supporting business clients.",
    format: ["6 modules", "12 learning hours", "Certificate of completion"],
    img: "/program-accounting.jpg",
    alt: "Modern glass office building illuminated at night",
    imgScale: 1.15,
    id: "digital-asset-accounting",
    href: "/programs/digital-asset-accounting",
  },
  {
    title: "Business Payments & Treasury",
    desc: "Understand how digital finance is changing payments, payouts, settlement, liquidity, and treasury workflows.",
    bestFor:
      "CFOs, treasury teams, founders, finance operations teams, and businesses managing money movement.",
    format: ["5 modules", "10 learning hours", "Certificate of completion"],
    img: "/program-payments.jpg",
    alt: "Digital payment dashboard on a laptop",
    id: "business-payments-treasury",
    href: "/programs/business-payments-and-treasury",
  },
  {
    title: "Compliance for Digital Finance Operations",
    desc: "Understand the controls, policies, and risk considerations behind digital finance workflows.",
    bestFor:
      "Compliance teams, CFOs, finance leaders, risk professionals, internal auditors, payment companies, and businesses preparing for digital finance operations.",
    format: ["5 modules", "10 learning hours", "Certificate of completion"],
    img: "/program-compliance.jpg",
    alt: "Panel discussion on governance, risk, and digital finance compliance",
    id: "compliance-digital-finance-operations",
    href: "/programs/compliance-for-digital-finance-operations",
  },
  {
    title: "Digital Asset Audit & Assurance",
    desc: "Learn how to evaluate digital asset records, transaction evidence, balances, controls, valuation, and reporting in an audit and assurance context.",
    bestFor:
      "Auditors, assurance professionals, internal audit teams, accounting firms, controllers, and finance professionals involved in audit preparation or review.",
    format: ["Online program", "Certificate of completion"],
    href: "/programs/digital-asset-audit-and-assurance",
    img: "/program-audit-assurance.jpg",
    alt: "Two professionals reviewing financial documents together at a table",
    id: "digital-asset-audit-assurance",
  },
];

const FeaturedProgramsList = () => {
  return (
    <section id="featured-programs" data-testid="programs-featured-section">
      <Container className="">
        <h2 className="text-center text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Upcoming Programs
        </h2>
        <div
          className="mx-auto mt-[14px] flex w-fit flex-col items-center gap-3 px-6 pt-4 sm:mt-7 sm:flex-row sm:gap-6 sm:px-8"
          data-testid="programs-featured-enrollment-banner"
        >
          <span className="flex items-center gap-2.5 text-sm font-medium text-foreground sm:text-base">
            <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--accent))] opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--accent))]" />
            </span>
            Programs begin in January 2027.
          </span>
          <span className="hidden h-5 w-px bg-[hsl(var(--accent))]/25 sm:block" aria-hidden="true" />
          <span className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-[hsl(var(--accent))] sm:text-base">
            <CalendarClock className="h-4 w-4 shrink-0" strokeWidth={1.9} />
            Applications are open until December 23, 2026.
          </span>
        </div>

        <div className="relative mt-[28px] flex flex-col gap-[28px] sm:mt-12 lg:hidden" data-testid="programs-mobile-list">
            {PROGRAM_CARDS.map((p) => (
          <article
            key={p.id}
            className="group flex w-full flex-col overflow-hidden rounded-2xl bg-card shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 transition-shadow hover:shadow-[0_28px_56px_-24px_hsl(218_51%_15%/0.4)]"
            data-testid={`programs-card-${p.id}`}
          >
            <div className="p-[18px] pb-0 sm:p-[22px] sm:pb-0">
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={p.img}
                  alt={p.alt}
                  className={
                    p.imgScale
                      ? "aspect-[2/1] w-full object-cover transition-transform duration-500 scale-[1.15] group-hover:scale-[1.185]"
                      : "aspect-[2/1] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  }
                  loading="lazy"
                  draggable={false}
                />
                <span
                  className="absolute inset-x-0 bottom-0 h-[3px] bg-[hsl(var(--accent))]"
                  aria-hidden="true"
                />
              </div>
            </div>
            <div className="flex flex-1 flex-col p-[18px] sm:p-[22px]">
              <h3 className="text-xl font-semibold leading-snug tracking-[-0.01em] text-foreground">
                {p.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>

              <div className="mt-6 space-y-4 border-t border-[hsl(var(--primary))]/10 pt-6">
                <div>
                  <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Best for
                  </span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">{p.bestFor}</p>
                </div>
                <div>
                  <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Format
                  </span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">
                    {p.format.join(" · ")}
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-1 items-end">
                {p.href ? (
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 w-full border-[hsl(var(--primary))]/25 bg-transparent px-6 text-sm font-medium transition-colors hover:bg-[hsl(var(--accent))] hover:text-white hover:border-transparent active:scale-[0.98]"
                    data-testid={`programs-card-${p.id}-view-button`}
                  >
                    <Link to={p.href}>View Program</Link>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="h-11 w-full border-[hsl(var(--primary))]/25 bg-transparent px-6 text-sm font-medium transition-colors hover:bg-[hsl(var(--accent))] hover:text-white hover:border-transparent active:scale-[0.98]"
                    onClick={inert}
                    data-testid={`programs-card-${p.id}-view-button`}
                  >
                    View Program
                  </Button>
                )}
              </div>
            </div>
          </article>
            ))}
        </div>

        {/* Desktop — compact editorial rows */}
        <div className="hidden lg:mt-14 lg:block lg:space-y-5" data-testid="programs-rows-list">
          {PROGRAM_CARDS.map((p) => {
            const RowTag = p.href ? Link : "div";
            const rowProps = p.href ? { to: p.href } : {};
            return (
            <RowTag
              key={p.id}
              {...rowProps}
              className={`group grid grid-cols-[210px_1fr_462px_36px] items-center gap-8 rounded-2xl border border-[hsl(var(--primary))]/10 bg-[hsl(174_45%_40%/0.05)] px-5 py-[18px] transition-all ${p.href ? "cursor-pointer hover:border-[hsl(var(--primary))]/20 hover:bg-[hsl(174_45%_40%/0.08)] hover:shadow-[0_20px_44px_-28px_hsl(218_51%_15%/0.28)]" : ""}`}
              data-testid={`programs-row-${p.id}`}
            >
              <div className="relative h-[161px] w-[210px] overflow-hidden rounded-xl">
                <img
                  src={p.img}
                  alt={p.alt}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                  loading="lazy"
                />
                <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[hsl(var(--accent))]" aria-hidden="true" />
              </div>

              <div className="min-w-0 pr-4">
                <h3 className="text-2xl font-semibold leading-snug tracking-[-0.01em] text-foreground">
                  {p.title}
                </h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>

              <div className="flex min-w-0 -ml-4 items-start gap-8 self-center">
                <div className="min-w-0 shrink-0 basis-[240px] border-l border-[hsl(var(--primary))]/10 pl-6">
                  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Best for
                  </span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/80">{p.bestFor}</p>
                </div>

                <div className="min-w-0 shrink-0 basis-[190px] border-l border-[hsl(var(--primary))]/10 pl-6">
                  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Format
                  </span>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/80">
                    {p.format.join(" · ")}
                  </p>
                </div>
              </div>

              <span
                aria-hidden="true"
                className={`flex h-9 w-9 self-end mb-3 items-center justify-center ${p.href ? "text-muted-foreground group-hover:text-[hsl(var(--accent))]" : "text-muted-foreground/50"}`}
                data-testid={`programs-row-${p.id}-view-button`}
              >
                <ArrowUpRight className="h-5 w-5 transition-colors" strokeWidth={1.75} />
              </span>
            </RowTag>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ================= Block 3 — Learning approach ================= */

const APPROACH = [
  {
    n: "01",
    title: "Business Finance Foundation",
    body: "Each program connects digital money, stablecoins, digital assets, and payment infrastructure to real business finance use cases.",
  },
  {
    n: "02",
    title: "Operational Clarity",
    body: "We focus on how new financial tools affect records, reconciliation, reporting, controls, treasury, and internal workflows.",
  },
  {
    n: "03",
    title: "Risk and Governance Awareness",
    body: "Programs introduce the key operational, compliance, documentation, and control considerations that finance teams should understand before adoption.",
  },
  {
    n: "04",
    title: "Professional Application",
    body: "Learning is designed for practical conversations with clients, leadership teams, auditors, payment providers, and compliance stakeholders.",
  },
  {
    n: "05",
    title: "Responsible Market Education",
    body: "WFII supports digital finance adoption through clear, vendor-neutral education grounded in business needs and professional standards.",
  },
];

const LearningApproach = () => (
  <section data-testid="programs-approach-section">
    <Container className="">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        Designed for Real{" "}
        <br className="sm:hidden" aria-hidden="true" />
        Finance Work
      </h2>
      <p className="mt-4 max-w-[62ch] text-base leading-relaxed text-muted-foreground lg:max-w-none lg:whitespace-nowrap lg:text-[18px]">
        A structured learning approach for professionals working with modern
        financial infrastructure.
      </p>
      <div className="mt-[46px] grid gap-x-10 gap-y-12 sm:mt-14 sm:grid-cols-2 lg:grid-cols-3">
        {APPROACH.map((a) => (
          <div
            key={a.n}
            className="group border-t border-[hsl(var(--accent))] pt-6"
            data-testid={`programs-approach-item-${a.n}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium tracking-[0.08em] text-muted-foreground">
                {a.n}
              </span>
              <span
                className="h-2 w-2 rounded-[2px] bg-[hsl(var(--accent))]/0 transition-colors group-hover:bg-[hsl(var(--accent))]/80"
                aria-hidden="true"
              />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">{a.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground lg:text-[16px]">{a.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 5 — Pricing ================= */

const ProgramsPricing = () => (
  <section data-testid="programs-pricing-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Pricing
        </h2>
      </div>

      <div className="mx-auto mt-[36px] grid max-w-4xl gap-6 sm:mt-14 sm:grid-cols-2">
        {/* Program Access */}
        <div
          className="flex flex-col rounded-2xl bg-primary p-8 text-primary-foreground shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-10"
          data-testid="programs-pricing-program-access-card"
        >
          <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
          <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-primary-foreground">
            Program Access
          </h3>
          <p className="mt-5 flex items-baseline gap-2">
            <span className="text-4xl font-semibold tracking-[-0.02em] text-primary-foreground">
              CAD $39
            </span>
          </p>
          <p className="mt-4 text-base leading-relaxed text-primary-foreground/75">
            Get full access to this program and all included learning materials.
          </p>
          <div className="mt-8 flex flex-1 items-end">
            <Button
              asChild
              className="h-11 w-full bg-white px-6 text-sm font-medium text-primary shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.25)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
              data-testid="programs-pricing-apply-now-button"
            >
              <Link to="/enrollment-request" state={{ type: "standard" }}>
                Apply Now
              </Link>
            </Button>
          </div>
        </div>

        {/* Join the WFII Professional Community */}
        <div
          className="flex flex-col rounded-2xl bg-card/80 p-8 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-10"
          data-testid="programs-pricing-community-card"
        >
          <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
          <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-foreground">
            Join the WFII Professional Community
          </h3>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Access member opportunities, professional resources, updates, and
            future programs.
          </p>
          <div className="mt-8 flex flex-1 items-end">
            <Button
              asChild
              className="h-11 w-full bg-[hsl(var(--accent))] px-6 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="programs-pricing-apply-membership-button"
            >
              <Link to="/enrollment-request" state={{ type: "membership" }}>
                Apply for Membership
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 6 — Certification pathways & corporate training ================= */

const PathwaysAndTraining = () => (
  <section data-testid="programs-pathways-section">
    <Container className="">
      <div className="divide-y divide-[hsl(var(--primary))]/12 border-y border-[hsl(var(--primary))]/12">
        {/* Certification Pathways */}
        <div
          className="flex flex-col gap-6 py-10 lg:flex-row lg:items-center lg:justify-between lg:gap-12"
          data-testid="programs-pathways-card"
        >
          <div className="max-w-2xl lg:max-w-4xl">
            <h2 className="text-left text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl">
              Certification Pathways
            </h2>
            <p className="mt-3 max-w-[58ch] text-left text-sm leading-relaxed text-muted-foreground sm:text-base lg:max-w-none">
              WFII programs can be completed individually or as part of a
              broader certification pathway for professionals building
              knowledge in digital finance.
            </p>
          </div>
          <div className="shrink-0 lg:w-[260px]">
            <Button
              asChild
              className="group h-11 w-full bg-[hsl(var(--accent))] px-6 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="programs-pathways-explore-button"
            >
              <Link to="/certification-pathways">Explore Certification Pathways</Link>
            </Button>
          </div>
        </div>

        {/* Corporate Training */}
        <div
          className="flex flex-col gap-6 py-10 lg:flex-row lg:items-center lg:justify-between lg:gap-12"
          data-testid="programs-corporate-training-card"
        >
          <div className="max-w-2xl lg:max-w-4xl">
            <h2 className="text-left text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl">
              Corporate Training
            </h2>
            <p className="mt-3 max-w-[58ch] text-left text-sm leading-relaxed text-muted-foreground sm:text-base lg:max-w-none">
              Tailored digital finance education for companies, accounting
              firms, fintech teams, and finance departments. Build shared
              knowledge across payments, stablecoins, accounting workflows,
              treasury operations, and compliance considerations.
            </p>
          </div>
          <div className="shrink-0 lg:w-[260px]">
            <Button
              asChild
              className="group h-11 w-full bg-[hsl(var(--accent))] px-6 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="programs-corporate-training-button"
            >
              <Link to="/enrollment-request" state={{ type: "corporate" }}>
                Request Corporate Training
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 7 — Final CTA ================= */

const ProgramsFinalCta = () => (
  <section data-testid="programs-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Start Building Confidence
            <br className="hidden lg:block" aria-hidden="true" /> in Digital Finance
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Join WFII to access member pricing, professional programs, and
              resources <br className="hidden lg:block" />
              <br className="sm:hidden" aria-hidden="true" />
              for modern finance workflows.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function ProgramsPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <ProgramsHero />
          <FeaturedProgramsList />
          <LearningApproach />
          <ProgramsPricing />
          <PathwaysAndTraining />
          <ProgramsFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
